import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  AutoAwesome as WandIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { specialRequestsApi, teamApi } from '../../firebase/services';
import { SpecialRequest, TeamMember } from '../../types';

const STATUS_CONFIG: Record<SpecialRequest['status'], { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }> = {
  new:       { label: 'Nouveau',   color: 'warning' },
  reviewed:  { label: 'Vu',        color: 'info' },
  contacted: { label: 'Contacté',  color: 'success' },
  declined:  { label: 'Décliné',   color: 'error' },
};

const SpecialRequestManagement: React.FC = () => {
  const [requests, setRequests] = useState<SpecialRequest[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SpecialRequest | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SpecialRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [statusEdit, setStatusEdit] = useState<SpecialRequest['status']>('new');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [reqs, members] = await Promise.all([specialRequestsApi.getAll(), teamApi.getAll()]);
    setRequests(reqs);
    setTeamMembers(members);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const getMemberName = (id?: string) => {
    if (!id) return '—';
    const m = teamMembers.find(t => t.id === id);
    return m ? m.name : '—';
  };

  const handleOpen = (req: SpecialRequest) => {
    setSelected(req);
    setAdminNotes(req.adminNotes || '');
    setStatusEdit(req.status);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    await specialRequestsApi.update(selected.id, { status: statusEdit, adminNotes });
    setSaving(false);
    setSelected(null);
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await specialRequestsApi.delete(deleteTarget.id);
    setDeleteTarget(null);
    load();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <WandIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={600} sx={{ color: '#495057' }}>
          Demandes spécialisées
        </Typography>
        {requests.filter(r => r.status === 'new').length > 0 && (
          <Chip
            label={`${requests.filter(r => r.status === 'new').length} nouveau${requests.filter(r => r.status === 'new').length > 1 ? 'x' : ''}`}
            color="warning"
            size="small"
          />
        )}
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Coiffeur souhaité</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Photo</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map(req => (
              <TableRow
                key={req.id}
                sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: '#f5f5f5' } }}
              >
                <TableCell sx={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
                  {format(new Date(req.createdAt), 'dd MMM yyyy', { locale: fr })}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{req.clientName}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{req.clientPhone}</Typography>
                  {req.clientEmail && (
                    <Typography variant="caption" color="text.secondary" display="block">{req.clientEmail}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 220 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                  >
                    {req.description}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: '#6c757d' }}>
                  {getMemberName(req.preferredStylistId)}
                </TableCell>
                <TableCell>
                  {req.photoUrl ? (
                    <Box
                      component="img"
                      src={req.photoUrl}
                      alt="ref"
                      sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                      onClick={() => window.open(req.photoUrl, '_blank')}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={STATUS_CONFIG[req.status].label}
                    color={STATUS_CONFIG[req.status].color}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Voir / modifier">
                      <IconButton size="small" sx={{ color: '#007bff' }} onClick={() => handleOpen(req)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" sx={{ color: '#dc3545' }} onClick={() => setDeleteTarget(req)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#6c757d', fontStyle: 'italic' }}>
                  Aucune demande spécialisée reçue
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail / Edit dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WandIcon color="primary" />
          Demande de {selected?.clientName}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
              {/* Photo */}
              {selected.photoUrl && (
                <Box
                  component="img"
                  src={selected.photoUrl}
                  alt="référence"
                  sx={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 2 }}
                />
              )}

              {/* Client info */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Nom</Typography>
                  <Typography variant="body2" fontWeight={600}>{selected.clientName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Téléphone</Typography>
                  <Typography variant="body2" fontWeight={600}>{selected.clientPhone}</Typography>
                </Box>
                {selected.clientEmail && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2">{selected.clientEmail}</Typography>
                  </Box>
                )}
                {selected.preferredDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Date souhaitée</Typography>
                    <Typography variant="body2">{format(new Date(selected.preferredDate + 'T00:00:00'), 'dd/MM/yyyy')}</Typography>
                  </Box>
                )}
                {selected.preferredTime && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Heure souhaitée</Typography>
                    <Typography variant="body2">{selected.preferredTime}</Typography>
                  </Box>
                )}
                {selected.preferredStylistId && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">Coiffeur souhaité</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                        {getMemberName(selected.preferredStylistId).charAt(0)}
                      </Avatar>
                      <Typography variant="body2">{getMemberName(selected.preferredStylistId)}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Description */}
              <Box>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, borderRadius: 1.5, bgcolor: '#fafafa' }}>
                  <Typography variant="body2">{selected.description}</Typography>
                </Paper>
              </Box>

              {/* Status */}
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select value={statusEdit} label="Statut" onChange={e => setStatusEdit(e.target.value as SpecialRequest['status'])}>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <MenuItem key={key} value={key}>
                      <Chip label={cfg.label} color={cfg.color} size="small" />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Admin notes */}
              <TextField
                label="Notes internes"
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="Rappeler le client, confirmer disponibilité..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Sauvegarder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Supprimer la demande</DialogTitle>
        <DialogContent>
          <Typography>
            Supprimer la demande de <strong>{deleteTarget?.clientName}</strong> ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpecialRequestManagement;
